import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { formatError } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { clearVerified, getVerified, isVerifyError, useStaff } from "@/lib/staff";
import { BTN, BTN_DANGER, BTN_DANGER_GHOST, BTN_GHOST, DIALOG } from "@/components/staff/ui";

// Every staff change goes through this: a confirm step with a written reason (saved in the audit log),
// and for role changes and deletions a confirmed identity first.
export function ConfirmAction({ label, icon: Icon, title, description, confirmLabel = "Confirm", danger, primary, needsVerify, run, children, testid, disabled, canSubmit = true }) {
  const { verify } = useStaff();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const start = async () => {
    if (needsVerify) {
      try { await verify.getToken(); } catch { return; }
    }
    setReason("");
    setOpen(true);
  };
  const submit = async (e) => {
    e.preventDefault();
    const token = needsVerify ? getVerified() : null;
    if (needsVerify && !token) {
      setOpen(false);
      toast.error("Your confirmation has run out. Please confirm it's you again.");
      return;
    }
    setBusy(true);
    try {
      await run(reason.trim(), token);
      setOpen(false);
    } catch (err) {
      if (isVerifyError(err)) clearVerified();
      toast.error(formatError(err));
    } finally { setBusy(false); }
  };
  const trigger = danger ? BTN_DANGER_GHOST : primary ? BTN : BTN_GHOST;
  return (
    <>
      <button type="button" onClick={start} disabled={disabled} className={trigger} data-testid={testid}>{Icon && <Icon className="h-4 w-4" />}{label}</button>
      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent className={DIALOG} data-testid={`${testid}-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-display">{title}</DialogTitle>
            {description && <DialogDescription className="text-neutral-400">{description}</DialogDescription>}
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            {children}
            <label className="block space-y-1.5">
              <span className="text-xs text-neutral-400">Reason (required, saved in the audit log)</span>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={500} placeholder="Why are you making this change?"
                className="bg-[#0B1220] border-[#22304B] text-sm" data-testid={`${testid}-reason`} />
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} disabled={busy} className={BTN_GHOST} data-testid={`${testid}-cancel`}>Cancel</button>
              <button type="submit" disabled={busy || reason.trim().length < 5 || !canSubmit} className={danger ? BTN_DANGER : BTN} data-testid={`${testid}-confirm`}>
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}{confirmLabel}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
